import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import FormGroup from 'components/FormGroup'
import Label from 'components/Label'
import { ErrorBoundary, HeaderFive } from 'secondstep-components'
import {
  AssignSchoolFieldBox,
  AssignSchoolContainer,
  Input,
  AssignSchoolSelect,
} from './component.styles'
import {
  ASSIGN_SCHOOL_HEADER,
  CITY_SELECT_DATA_TEST_ID,
  CITY_SELECT_LABEL,
  COUNTRY_SELECT_DATA_TEST_ID,
  COUNTRY_SELECT_LABEL,
  REGION_SELECT_DATA_TEST_ID,
  REGION_SELECT_LABEL,
  SCHOOL_IS_NOT_LISTED_WARNING,
  SCHOOL_SELECT_DATA_TEST_ID,
  SCHOOL_NAME_SELECT_LABEL,
  SCHOOL_SELECT_LABEL,
} from './constants'
import { useLocationData } from './hooks/useLocationData'

const AssignSchool = ({ onFormChange, hasRegionData, siteData }) => {
  const { city, country, netSuiteCustomerId, siteName, region } = siteData

  const {
    countries,
    regions,
    cities,
    schools,
    loadingStates,
    error,
  } = useLocationData(country, region, city, hasRegionData, onFormChange)

  const handleFieldChange = useCallback(e => onFormChange(e, 'field'), [
    onFormChange,
  ])

  // Location field configurations
  const fields = useMemo(
    () => ({
      country: {
        name: 'country',
        dataTestId: COUNTRY_SELECT_DATA_TEST_ID,
        label: COUNTRY_SELECT_LABEL,
        value: country,
        data: countries,
        loadingKey: 'countries',
      },
      region: {
        name: 'region',
        dataTestId: REGION_SELECT_DATA_TEST_ID,
        label: REGION_SELECT_LABEL,
        value: region,
        data: regions,
        loadingKey: 'regions',
        disabled: !hasRegionData || !country,
      },
      city: {
        name: 'city',
        dataTestId: CITY_SELECT_DATA_TEST_ID,
        label: CITY_SELECT_LABEL,
        value: city,
        data: cities,
        loadingKey: 'cities',
        disabled: !country || (hasRegionData && !region),
      },
    }),
    [country, region, city, countries, regions, cities, hasRegionData],
  )

  const renderLocationSelect = field => (
    <AssignSchoolFieldBox key={field.name}>
      <AssignSchoolSelect
        data={field.data}
        dataTestId={field.dataTestId}
        disabled={field.disabled}
        error={error}
        isLoading={loadingStates[field.loadingKey]}
        label={field.label}
        name={field.name}
        onChange={handleFieldChange}
        value={field.value}
      />
    </AssignSchoolFieldBox>
  )

  return (
    <ErrorBoundary>
      <AssignSchoolContainer>
        {renderLocationSelect(fields.country)}
        {renderLocationSelect(fields.region)}
      </AssignSchoolContainer>
      <AssignSchoolContainer>
        {renderLocationSelect(fields.city)}
        <AssignSchoolFieldBox>
          <AssignSchoolSelect
            data={schools}
            dataTestId={SCHOOL_SELECT_DATA_TEST_ID}
            disabled={!city || !country}
            error={error}
            isLoading={loadingStates.schools}
            label={SCHOOL_SELECT_LABEL}
            name="id"
            onChange={e => onFormChange(e, 'school')}
            renderOptions={value => (
              <option key={value.id} value={value.id}>
                {value.companyName}
              </option>
            )}
            value={netSuiteCustomerId?.toString() || ''}
            warning={
              netSuiteCustomerId === 0 ? SCHOOL_IS_NOT_LISTED_WARNING : null
            }
          />
        </AssignSchoolFieldBox>
      </AssignSchoolContainer>
      {netSuiteCustomerId === 0 && (
        <FormGroup>
          <HeaderFive>{ASSIGN_SCHOOL_HEADER}</HeaderFive>
          <Label for="siteName">{SCHOOL_NAME_SELECT_LABEL}</Label>
          <Input
            id="siteName"
            name="siteName"
            onChange={handleFieldChange}
            value={siteName}
          />
        </FormGroup>
      )}
    </ErrorBoundary>
  )
}

AssignSchool.propTypes = {
  hasRegionData: PropTypes.bool.isRequired,
  onFormChange: PropTypes.func.isRequired,
  siteData: PropTypes.shape({
    city: PropTypes.string,
    country: PropTypes.string,
    netSuiteCustomerId: PropTypes.number,
    nsCustomerId: PropTypes.string,
    region: PropTypes.string,
    siteName: PropTypes.string,
  }),
}

export default AssignSchool
